import { Injectable } from '@angular/core';
import {
  CreateSendout,
  Event,
  Sendout,
  Store,
  Template,
  UserType,
  UserTypeEnum,
} from '../../types';
import { BehaviorSubject } from 'rxjs';
import type { EffectiveAccess } from '../../guards/auth/roles';

export type SessionData = Partial<EffectiveAccess> & {
  initialized: boolean;
  /** Provider credentials are never retained by the SAM application. */
  token: string;
  sessionVersion?: string;
  csrfToken?: string;
  expiresAt?: string;
  userId: number;
  userType: UserTypeEnum;
  store: Store | null;
  storeImpersonation: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class GlobalDataService {
  private sessionData$: BehaviorSubject<SessionData>;
  private storeImpersonation$: BehaviorSubject<boolean>;
  private sendoutId: number | null = null;
  private sendout: CreateSendout | Sendout | null = null;
  private event: Event | null = null;
  private sendoutDate: string | null = null;
  private authenticationGeneration = 0;

  constructor() {
    this.sessionData$ = new BehaviorSubject<SessionData>(
      {
        initialized: false,
        token: '',
        userId: 0,
        userType: UserType.Unknown,
        store: null,
        storeImpersonation: false,
        roles: [],
      }
    );
    this.storeImpersonation$ = new BehaviorSubject<boolean>(false);
    this.sessionData$.subscribe((data) => {
      if (data.initialized) {
        if (data.storeImpersonation != this.storeImpersonation$.value) {
          this.storeImpersonation$.next(data.storeImpersonation);
        }
        sessionStorage.removeItem('sessionData');
      } else {
        sessionStorage.removeItem('sessionData');
      }
    });
  }

  getSessionData(): BehaviorSubject<SessionData> {
    return this.sessionData$;
  }

  setSessionData(data: SessionData): void {
    const previous = this.sessionData$.value;
    if (previous.userId !== data.userId || previous.userType !== data.userType) {
      this.clearSendoutContext();
    }
    this.sessionData$.next(data);
  }
  beginAuthentication(): number {
    return ++this.authenticationGeneration;
  }

  isAuthenticationCurrent(generation: number): boolean {
    return generation === this.authenticationGeneration;
  }

  getAuthenticationGeneration(): number {
    return this.authenticationGeneration;
  }

  clearSessionData(): void {
    this.authenticationGeneration++;
    this.setSessionData({
      initialized: false,
      token: '',
      userId: 0,
      userType: UserType.Unknown,
      store: null,
      storeImpersonation: false,
      roles: [],
    });
    this.storeImpersonation$.next(false);
  }

  getStoreImpersonation(): BehaviorSubject<boolean> {
    return this.storeImpersonation$;
  }

  private clearSendoutContext(): void {
    this.setSendout(null);
    this.setSendoutId(null);
    this.setEvent(null);
    this.setSendoutDate(null);
  }

  selectEditorStore(store: Store): void {
    if (this.sessionData$.value.store?.id !== store.id) this.clearSendoutContext();
    this.setSessionData({ ...this.sessionData$.value, store, storeImpersonation: false });
  }

  startStoreImpersonation(store: Store): void {
    this.clearSendoutContext();
    this.setSessionData({
      ...this.sessionData$.value,
      storeImpersonation: true,
      store: store,
    });
  }

  stopStoreImpersonation(): void {
    this.clearSendoutContext();
    this.setSessionData({
      ...this.sessionData$.value,
      storeImpersonation: false,
      store: null,
    });
  }

  setSendoutDate(date: string | null) {
    this.sendoutDate = date;
  }

  getSendoutDate() {
    return this.sendoutDate;
  }

  setEvent(event: Event | null): void {
    this.event = event;
  }

  getEvent(): Event | null {
    return this.event;
  }

  setSendout(sendout: CreateSendout | Sendout | null): void {
    this.sendout = sendout;
  }

  getSendout(): CreateSendout | Sendout | null {
    return this.sendout;
  }

  setSendoutId(sendoutId: number | null): void {
    this.sendoutId = sendoutId;
  }

  getSendoutId(): number | null {
    return this.sendoutId;
  }
}
